import { PagedApiResponse, WeightingScheme, WeightingSchemeGoalComplex, WeightingSchemeGoalSimple } from "types";

import API from "api/client";
import { envConfig } from "config";
import { toObject } from "@civicscience/chops";

const { weightingSchemesAPI } = envConfig;

type WeightingSchemeCreate = Omit<WeightingScheme, "segmentUuid" | "schemeType"> & {
  segmentUuid?: WeightingScheme["segmentUuid"];
  schemeType?: WeightingScheme["schemeType"];
};

type GetWeightingSchemesArgs = {
  query: string;
  showDeleted?: boolean;
  showUnlabeled?: boolean;
  page?: number;
  size?: number;
};

type UpdateWeightingSchemeArgs = {
  description: string;
  label: string;
  isDeleted: boolean;
  segmentUuid?: string;
  schemeType: string;
  deleteAccounts: number[];
  createAccounts: number[];
};

type UpdateGoalsArgs = {
  weightingSchemeGoals: WeightingSchemeGoalSimple[];
  complexWeightingSchemeGoals: WeightingSchemeGoalComplex[];
};

/**
 * Creates a new Weighting Scheme
 */
const createWeightingScheme = (req: WeightingSchemeCreate): Promise<WeightingScheme> => {
  return API.post(`${weightingSchemesAPI}`, req).then((res) => res.data);
};

/**
 * Fetches the fully populated weighting scheme including associated accounts.
 */
const getEntireWeightingScheme = (name: string): Promise<WeightingScheme> => {
  return API.get(`${weightingSchemesAPI}${name}`).then((res) => res.data);
};

/**
 * Searches weighting schemes based on the provided parameters.
 *
 * NOTE: This endpoint is currently hardcoded to check for admin status.
 * If support for non-admin users is required in the future, consider
 * adding a corresponding flag or parameter to GetWeightingSchemesArgs.
 */
const getAdminWeightingSchemes = (options: GetWeightingSchemesArgs): Promise<PagedApiResponse<WeightingScheme[]>> => {
  const { query, showDeleted, showUnlabeled, page = 1, size = 2000 } = toObject(options);
  return API.get(`${weightingSchemesAPI}`, {
    params: { query, showDeleted, showUnlabeled, page, size, admin: true },
  }).then((res) => res.data);
};

/**
 * Fetches all weighting scheme instances for the user.
 */
const getAllWeightingSchemes = (includeAccountDefault = false): Promise<WeightingScheme[]> => {
  return API.get(`${weightingSchemesAPI}all`, { params: { includeAccountDefault } }).then((res) => res.data);
};

/**
 * Updates the weighting scheme by `name` with the provided params.
 */
const updateWeightingScheme = (name: string, req: UpdateWeightingSchemeArgs): Promise<WeightingScheme> => {
  return API.put(`${weightingSchemesAPI}${name}`, req).then((res) => res.data);
};

/**
 * Uploads a new goals file.
 */
const uploadFileGoals = (name: string, formData: FormData) => {
  return API.put(`${weightingSchemesAPI}${name}/file-import`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((res) => res.data);
};

/**
 * Updates the goals of a weighting scheme with the provided params.
 */
const updateGoals = (name: string, req: UpdateGoalsArgs): Promise<WeightingScheme> => {
  return API.put(`${weightingSchemesAPI}${name}/goals`, req).then((res) => res.data);
};

export default {
  create: createWeightingScheme,
  get: getEntireWeightingScheme,
  adminList: getAdminWeightingSchemes,
  getAll: getAllWeightingSchemes,
  update: updateWeightingScheme,
  uploadFileGoals,
  updateGoals,
};
